/**
 * Krok 3: Twoje psy (in-wizard quick add list)
 * Pozwala dodać kilku psów z minimalnymi danymi: zdjęcie, imię, rola, opis, osiągnięcia, galeria.
 * Lista dodanych psów widoczna u góry; klik "Dodaj psa" otwiera mini-formularz.
 */
import { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dog as DogIcon, Plus, X, Camera, Trash2, Loader2, Award, Edit2, ImagePlus, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { chunkedUpload, validateFile } from '@/utils/chunkedUpload';
import { getImageUrl } from '@/utils/imageUrl';

const ROLES = [
  { id: 'reproduktor', label: 'Reproduktor' },
  { id: 'suka', label: 'Suka hodowlana' },
  { id: 'mlody', label: 'Młody pies' },
  { id: 'champion', label: 'Champion' },
  { id: 'inny', label: 'Inny' },
];

const NAME_MAX = 60;
const DESC_MAX = 250;

function DogForm({ dog, onSave, onCancel }) {
  const [form, setForm] = useState(
    dog || { photo: null, name: '', role: 'reproduktor', description: '', achievements: [], gallery: [] }
  );
  const [achievementInput, setAchievementInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handlePhoto = async (file) => {
    if (!file) return;
    const v = validateFile(file);
    if (!v.valid) return toast.error(v.error);
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await chunkedUpload(file, token);
      setForm((p) => ({ ...p, photo: r.urls?.large || r.url }));
      toast.success('Zdjęcie dodane');
    } catch { toast.error('Błąd uploadu'); }
    finally { setUploading(false); }
  };

  const handleGallery = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const token = localStorage.getItem('token');
    const newUrls = [];
    for (const file of files) {
      const v = validateFile(file);
      if (!v.valid) continue;
      try {
        const r = await chunkedUpload(file, token);
        newUrls.push(r.urls?.medium || r.url);
      } catch {}
    }
    setForm((p) => ({ ...p, gallery: [...(p.gallery || []), ...newUrls].slice(0, 6) }));
    setUploading(false);
    if (newUrls.length) toast.success(`Dodano ${newUrls.length} zdjęć`);
  };

  const addAchievement = () => {
    const v = achievementInput.trim();
    if (!v) return;
    setForm((p) => ({ ...p, achievements: [...(p.achievements || []), v] }));
    setAchievementInput('');
  };

  const removeAchievement = (idx) => {
    setForm((p) => ({ ...p, achievements: p.achievements.filter((_, i) => i !== idx) }));
  };

  const removeGalleryImg = (idx) => {
    setForm((p) => ({ ...p, gallery: p.gallery.filter((_, i) => i !== idx) }));
  };

  const save = () => {
    if (!form.name.trim()) return toast.error('Podaj imię psa');
    if (!form.role) return toast.error('Wybierz rolę');
    onSave(form);
  };

  return (
    <div className="rounded-2xl border-2 border-orange-200 bg-white shadow-lg p-5 space-y-5" data-testid="wizard-v2-dog-form">
      {/* 1. Zdjęcie główne */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">1. Dodaj zdjęcie psa (główne)</Label>
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e.target.files?.[0])} />
        {form.photo ? (
          <div className="relative rounded-xl overflow-hidden border-2 border-orange-200 aspect-[16/9] bg-gray-100">
            <img src={getImageUrl(form.photo) || form.photo} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => photoInputRef.current?.click()} className="absolute bottom-3 left-3 bg-white shadow-md px-3 py-1.5 rounded-lg text-xs font-medium text-gray-800 flex items-center gap-1">
              <Camera className="w-3.5 h-3.5" /> Zmień zdjęcie
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploading}
            data-testid="wizard-v2-dog-photo-btn"
            className="w-full aspect-[16/9] rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 hover:border-orange-400 flex flex-col items-center justify-center gap-2"
          >
            {uploading ? <Loader2 className="w-8 h-8 text-orange-500 animate-spin" /> : (
              <>
                <ImagePlus className="w-8 h-8 text-orange-500" />
                <p className="text-sm font-semibold text-orange-600">Dodaj zdjęcie</p>
                <p className="text-xs text-gray-500">lub przeciągnij tutaj</p>
              </>
            )}
          </button>
        )}
      </div>

      {/* 2. Imię */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-900">2. Jak ma na imię Twój pies?</Label>
          <span className="text-xs text-gray-400">{(form.name || '').length}/{NAME_MAX}</span>
        </div>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="np. Złoty Marzenie FCI Ares"
          maxLength={NAME_MAX}
          data-testid="wizard-v2-dog-name"
          className="h-11 focus-visible:ring-orange-400"
        />
      </div>

      {/* 3. Rola */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">3. Kim jest?</Label>
        <div className="flex flex-wrap gap-2">
          {ROLES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setForm({ ...form, role: r.id })}
              data-testid={`wizard-v2-dog-role-${r.id}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                form.role === r.id
                  ? 'border-orange-400 bg-orange-50 text-orange-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* 4. Opis */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-gray-900">4. Co wyróżnia tego psa?</Label>
          <span className="text-xs text-gray-400">{(form.description || '').length}/{DESC_MAX}</span>
        </div>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="charakter, linia, mocne strony…"
          rows={3}
          maxLength={DESC_MAX}
          data-testid="wizard-v2-dog-description"
          className="resize-none focus-visible:ring-orange-400"
        />
      </div>

      {/* 5. Osiągnięcia */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">5. Najważniejsze osiągnięcia i badania</Label>
        <div className="flex flex-wrap gap-2">
          {(form.achievements || []).map((a, i) => (
            <span key={i} className="px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs rounded-md flex items-center gap-1">
              <Award className="w-3 h-3" /> {a}
              <button type="button" onClick={() => removeAchievement(i)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={achievementInput}
            onChange={(e) => setAchievementInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAchievement(); } }}
            placeholder="np. Champion Polski, HD-A, ED-0/0"
            data-testid="wizard-v2-dog-achievement-input"
            className="h-10 focus-visible:ring-orange-400"
          />
          <Button type="button" variant="outline" onClick={addAchievement} className="border-orange-200 text-orange-600">
            <Plus className="w-4 h-4" /> Dodaj
          </Button>
        </div>
      </div>

      {/* 6. Galeria psa */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-900">6. Dodaj więcej zdjęć (galeria psa)</Label>
        <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleGallery(Array.from(e.target.files || []))} />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {(form.gallery || []).map((img, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 group">
              <img src={getImageUrl(img) || img} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeGalleryImg(i)} className="absolute top-1 right-1 w-6 h-6 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100">
                <X className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ))}
          {(form.gallery || []).length < 6 && (
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              data-testid="wizard-v2-dog-gallery-add"
              className="aspect-square rounded-lg border-2 border-dashed border-orange-300 bg-orange-50/40 hover:bg-orange-50 flex flex-col items-center justify-center text-orange-500"
            >
              <Plus className="w-5 h-5" />
              <span className="text-[10px] font-medium mt-1">Dodaj zdjęcia</span>
            </button>
          )}
        </div>
      </div>

      {/* Save row */}
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="wizard-v2-dog-cancel">Anuluj</Button>
        <Button type="button" onClick={save} disabled={uploading} data-testid="wizard-v2-dog-save" className="bg-orange-500 hover:bg-orange-600 text-white">
          <Check className="w-4 h-4 mr-1.5" /> Zapisz psa
        </Button>
      </div>
    </div>
  );
}

export default function Step3Dogs({ data, updateData }) {
  const dogs = data.dogs || [];
  const [editingIdx, setEditingIdx] = useState(dogs.length === 0 ? -1 : null); // -1 = new dog, null = list view

  const saveDog = (dog) => {
    if (editingIdx === -1) {
      updateData({ dogs: [...dogs, dog] });
    } else {
      updateData({ dogs: dogs.map((d, i) => (i === editingIdx ? dog : d)) });
    }
    setEditingIdx(null);
  };

  const deleteDog = (idx) => {
    updateData({ dogs: dogs.filter((_, i) => i !== idx) });
  };

  const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.id, r.label]));

  return (
    <div className="space-y-6" data-testid="wizard-v2-step-dogs">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <DogIcon className="w-5 h-5 text-orange-500" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Pokaż swoje psy</h2>
        </div>
        <p className="text-gray-500 text-sm">
          Dodaj swoje reproduktory, suki hodowlane i psy, z których jesteś dumny. Możesz pominąć ten krok i dodać później.
        </p>
      </header>

      {/* Lista dodanych psów */}
      {dogs.length > 0 && editingIdx === null && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            Dodane psy ({dogs.length})
          </p>
          {dogs.map((dog, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white hover:border-orange-200 hover:shadow-sm transition-all"
              data-testid={`wizard-v2-dog-card-${i}`}
            >
              <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                {dog.photo ? (
                  <img src={getImageUrl(dog.photo) || dog.photo} alt={dog.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-orange-50">
                    <DogIcon className="w-6 h-6 text-orange-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{dog.name}</p>
                <p className="text-xs text-gray-500">
                  {ROLE_LABEL[dog.role] || dog.role}
                  {dog.achievements?.length ? ` · ${dog.achievements.length} osiągnięć` : ''}
                </p>
              </div>
              <button type="button" onClick={() => setEditingIdx(i)} className="p-2 text-gray-400 hover:text-orange-500" data-testid={`wizard-v2-dog-edit-${i}`}>
                <Edit2 className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => deleteDog(i)} className="p-2 text-gray-400 hover:text-red-600" data-testid={`wizard-v2-dog-delete-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setEditingIdx(-1)}
            data-testid="wizard-v2-add-dog-btn"
            className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj kolejnego psa
          </Button>
        </div>
      )}

      {/* Form */}
      {editingIdx !== null && (
        <DogForm
          dog={editingIdx >= 0 ? dogs[editingIdx] : null}
          onSave={saveDog}
          onCancel={() => setEditingIdx(dogs.length === 0 ? null : null)}
        />
      )}

      {/* Empty state */}
      {dogs.length === 0 && editingIdx === null && (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center">
          <DogIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-3">Nie dodałeś jeszcze żadnego psa</p>
          <Button type="button" onClick={() => setEditingIdx(-1)} className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1.5" /> Dodaj pierwszego psa
          </Button>
        </div>
      )}
    </div>
  );
}
